import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { TextField, TextFieldProps, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useGetJiraStatusesByProjectId } from '../JiraConnect.hooks';
import { JiraStatus } from '../JiraConnect.interface';

const StyledTypography = styled(Typography)`
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface BaseJiraStatusPickerProps {
  projectId?: string;
  issueTypeId?: string;
  disabled?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
}

export interface JiraStatusSinglePickerProps extends BaseJiraStatusPickerProps {
  value?: string;
  multiple?: false;
  onChange?: (status: JiraStatus | undefined) => void;
}

export interface JiraStatusMultiplePickerProps
  extends BaseJiraStatusPickerProps {
  value?: string[];
  multiple: true; // always true for multiple
  onChange?: (statuses: JiraStatus[]) => void;
}

export type JiraStatusPickerProps =
  | JiraStatusSinglePickerProps
  | JiraStatusMultiplePickerProps;

const JiraStatusPicker: React.FC<JiraStatusPickerProps> = props => {
  const { value, disabled, multiple, projectId, issueTypeId, textFieldProps } =
    props;
  const [{ data, isLoading, error }, getStatuses] =
    useGetJiraStatusesByProjectId(projectId || '');

  const options = useMemo(() => {
    const issueTypes = data || [];
    // if issueTypeId filter is not provided, merge all statuses by id
    if (issueTypeId) {
      const issueType = issueTypes.find(
        issueType => issueType.id === issueTypeId
      );
      return issueType?.statuses || [];
    }
    return issueTypes.reduce((statuses: JiraStatus[], issueType) => {
      const newStatuses = (issueType.statuses || []).filter(
        status => !statuses.some(s => s.id === status.id)
      );
      return statuses.concat(newStatuses);
    }, []);
  }, [data, issueTypeId]);

  const selected = useMemo(() => {
    const ids = Array.isArray(value) ? value : value ? [value] : [];
    const selected = options.filter(option => ids.includes(option.id));
    return multiple ? selected : selected[0] || null;
  }, [multiple, options, value]);

  useEffect(() => {
    getStatuses();
  }, [getStatuses]);

  return (
    <Autocomplete
      fullWidth
      className='field'
      value={selected}
      options={options}
      disabled={disabled}
      loading={isLoading}
      multiple={multiple}
      getOptionLabel={option => option.name}
      onChange={(_, value) => {
        const statuses = Array.isArray(value) ? value : value ? [value] : [];
        if (props.multiple) {
          props.onChange?.(statuses);
        } else {
          props.onChange?.(statuses[0]);
        }
      }}
      renderTags={(value, getTagProps) => {
        return value.map((option, index, arr) => {
          return (
            <StyledTypography {...getTagProps({ index })}>
              {option.name}
              {index < arr.length - 1 ? ',' : ''}
            </StyledTypography>
          );
        });
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Status'
          error={!!error?.message}
          helperText={error?.message}
          {...textFieldProps}
          InputProps={{ ...params.InputProps, ...textFieldProps?.InputProps }}
        />
      )}
    />
  );
};

export default JiraStatusPicker;
